import { Box, BoxProps } from "@mui/material";
import { ReactNode } from "react";

import { UseApiRequestBaseResult } from "@/common/hooks/api/useApiRequest";
import { DisplayProps } from "@/common/hooks/useDisplayProps";
import {
  BaseEntityDataDto,
  BaseEntityDto,
  BaseEntityFullRefDto,
  BaseEntityRefDto,
} from "@/core/api/generated/v1";

import AppIcon from "../../../Icons/AppIcon";
import TextLineSkeleton from "../../../Skeleton/TextLineSkeleton";
import AppTypography from "../../../Text/AppTypography";
import AppTooltip, { AppTooltipProps } from "../../../Tooltip/AppTooltip";
import { BaseEntityViewInheritableProps } from "../BaseEntityView";

export interface BaseEntityInlineProps {
  entity:
    | BaseEntityDto
    | BaseEntityRefDto
    | BaseEntityFullRefDto
    | BaseEntityDataDto
    | null
    | undefined;
  entityId: string | null | undefined;
  request?: UseApiRequestBaseResult;
  content: ReactNode;
  icon?: ReactNode;
  withEllipsing?: boolean;
  withIcon?: boolean;
  withTooltip?: boolean;
  withActions?: boolean;
  withMenu?: boolean;
  defaultTooltipTitle?: ReactNode;
  tooltipTitle?: ReactNode;
  tooltipProps?: Partial<AppTooltipProps>;
  actions?: ReactNode;
  menu?: ReactNode;
  onClick?: BoxProps["onClick"];
}

export interface BaseEntityInlineInheritableProps {
  content?: ReactNode;
  fetch?: boolean;
  withEllipsing?: boolean;
  withIcon?: boolean;
  withTooltip?: boolean;
  withActions?: boolean;
  withMenu?: boolean;
  displayProps?: Partial<DisplayProps>;
  tooltipTitle?: ReactNode;
  tooltipProps?: Partial<AppTooltipProps>;
  /** Entity view props. */
  viewProps?: Partial<BaseEntityViewInheritableProps<BaseEntityDto>>;
  onClick?: BoxProps["onClick"];
}

/** Inline brief information about the entity. */
export default function BaseEntityInline({
  entity,
  entityId,
  request,
  content,
  icon,
  withEllipsing = false,
  withIcon = true,
  withTooltip = false,
  withActions = false,
  withMenu = false,
  defaultTooltipTitle,
  tooltipTitle,
  tooltipProps,
  actions,
  menu,
  onClick,
}: BaseEntityInlineProps) {
  const entityIdComputed = entity?.id || entityId || undefined;
  const isRequestFailed =
    (request && request.isEnded && request.responseDetails && !request.responseDetails.isOk) ??
    false;

  if (!entity && !entityId && !entityIdComputed) {
    return null;
  }
  if (request && !request.isEnded && request.isLoading) {
    return (
      <TextLineSkeleton sx={{ display: "inline-block", minWidth: 40, maxWidth: "fit-content" }} />
    );
  }
  if (!entity && !isRequestFailed && !content) {
    return null;
  }

  return (
    <AppTypography
      component='span'
      isInheritFontStyle
      ellipsing={{ enabled: withEllipsing }}
      onClick={onClick}
    >
      {isRequestFailed && (
        <Box component='span'>
          {withIcon && (
            <AppTypography isInheritFontStyle component='span' sx={{ mr: 1 }}>
              <AppIcon of='error' inText />
            </AppTypography>
          )}

          <Box component='span'>
            {(request?.responseDetails?.isBadRequest && "Bad request") ||
              (request?.responseDetails?.isUnauthorized && "Login required") ||
              (request?.responseDetails?.isUnauthorized && "Login required") ||
              (request?.responseDetails?.isForbidden && "Insufficient permissions") ||
              (request?.responseDetails?.isNotFound && "Entity not found") ||
              (request?.responseDetails?.isInternalServerError && "Internal error") ||
              "Error"}
          </Box>
        </Box>
      )}

      {!isRequestFailed && (
        <Box component='span' sx={{ display: "inline-flex", alignItems: "center" }}>
          <AppTooltip
            isEnabled={withTooltip}
            title={tooltipTitle || defaultTooltipTitle}
            wrapperProps={{ component: "span" }}
            {...tooltipProps}
          >
            <Box component='span' sx={{ display: "inline-flex", alignItems: "center" }}>
              {withIcon && icon && (
                <Box component='span' sx={{ mr: 0.5 }}>
                  {icon}
                </Box>
              )}

              <Box component='span'>{content}</Box>
            </Box>
          </AppTooltip>

          {/* NB: actions and menu might have own tooltips. */}
          {withActions && (
            <Box component='span' sx={{ ml: 0.5 }}>
              {actions}
            </Box>
          )}

          {withMenu && (
            <Box component='span' sx={{ ml: 0.5 }}>
              {menu}
            </Box>
          )}
        </Box>
      )}
    </AppTypography>
  );
}
